// --- SHARED CATEGORY TAXONOMY & THEME HELPERS ---
// Extracted into its own module (rather than living in App.tsx) so that
// other components — like ActiveSellerDashboard.tsx — can import it
// without creating a circular dependency (App.tsx -> SellerPortal.tsx ->
// ActiveSellerDashboard.tsx -> App.tsx).

// Sub-categories that are switched OFF for now. They stay in the taxonomy
// below (and in the database) so existing records still resolve to a name and
// a parent, but they are hidden everywhere in the app: buyer menus, filters,
// search and feeds, and the seller's Create Listing picker. The matching
// database trigger (see the SQL notes) also refuses new listings in them.
//
// TO BRING ONE BACK: delete its id from this set and drop the trigger.
export const DISABLED_SUBCATEGORY_IDS = new Set<string>([
  "baby_sitting",
  "pet_sitting",
]);

export const isSubcategoryDisabled = (rawSubcategory?: string) =>
  DISABLED_SUBCATEGORY_IDS.has((rawSubcategory || "").toLowerCase());

type CategoryDef = {
  id: string;
  name: string;
  abbr: string;
  color: string;
  statement: string;
  subcategories: { id: string; name: string }[];
};

// FULL taxonomy, including the switched-off sub-categories. Only used to
// build the lookups below; the app itself reads CATEGORIES (further down).
const ALL_CATEGORIES: Record<string, CategoryDef> = {
  home_services: {
    id: "home_services",
    name: "HOME SERVICES",
    abbr: "HS",
    color: "#0ea5e9",
    statement: "Everyday help, sorted.",
    subcategories: [
      { id: "laundry", name: "Laundry" },
      { id: "home_cleaning", name: "Home Cleaning" },
      { id: "baby_sitting", name: "Baby Sitting" },
      { id: "pet_sitting", name: "Pet Sitting" },
      { id: "moving_help", name: "Moving Help" },
      { id: "errand_running", name: "Errand Running" },
    ],
  },
  fashion_beauty: {
    id: "fashion_beauty",
    name: "FASHION & BEAUTY",
    abbr: "FB",
    color: "#f97316",
    statement: "Style that speaks for itself.",
    subcategories: [
      { id: "fashion", name: "Clothing" },
      { id: "jewellery", name: "Jewellery & Accessories" },
      { id: "shoes", name: "Shoes & Sneakers" },
      { id: "hair_makeup", name: "Hair & Makeup" },
      { id: "nails", name: "Nails" },
    ],
  },
  furniture_home_goods: {
    id: "furniture_home_goods",
    name: "FURNITURE & HOME GOODS",
    abbr: "FH",
    // Was brown (#8B4513) — too muddy to carry an entire dashboard theme,
    // so the whole Furniture & Home Goods theme (buyer badges included)
    // now runs on teal instead.
    color: "#14b8a6",
    statement: "Customize your space.",
    subcategories: [
      { id: "furniture", name: "Furniture" },
      { id: "home_decor", name: "Home Decor" },
      { id: "kitchenware", name: "Kitchenware" },
    ],
  },
  food_drinks: {
    id: "food_drinks",
    name: "FOOD & DRINKS",
    abbr: "FD",
    color: "#22c55e",
    statement: "Feed the hustle.",
    subcategories: [
      { id: "baked_goods", name: "Pastries" },
      { id: "meal_prep", name: "Meals" },
      { id: "drinks_juices", name: "Drinks & Fruits" },
    ],
  },
  tech_electronics: {
    id: "tech_electronics",
    name: "TECH & ELECTRONICS",
    abbr: "TE",
    color: "#6366f1",
    statement: "Gadgets, fixed and flipped.",
    subcategories: [
      { id: "phone_repair", name: "Phone Repair" },
      { id: "laptop_repair", name: "Laptop Repair" },
      { id: "gadgets_accessories", name: "Gadgets & Accessories" },
    ],
  },
  academic_services: {
    id: "academic_services",
    name: "ACADEMIC SERVICES",
    abbr: "AS",
    color: "#a855f7",
    statement: "Smarter, together.",
    subcategories: [
      { id: "typing_binding", name: "Typing & Binding" },
      { id: "academic_printing_services", name: "Printing Services" },
      { id: "kra_filing", name: "KRA Filing" },
      { id: "hef_services", name: "HEF Services" },
      { id: "kuccps_services", name: "KUCCPS Services" },
    ],
  },
  events_creative: {
    id: "events_creative",
    name: "EVENTS & CREATIVE",
    abbr: "EC",
    color: "#ec4899",
    statement: "Make it memorable.",
    subcategories: [
      { id: "photography", name: "Photography" },
      { id: "graphic_design", name: "Graphic Design" },
    ],
  },
};

// What the app actually shows: the same categories, minus switched-off
// sub-categories.
export const CATEGORIES: Record<string, CategoryDef> = Object.fromEntries(
  Object.entries(ALL_CATEGORIES).map(([key, cat]) => [
    key,
    {
      ...cat,
      subcategories: cat.subcategories.filter(
        (sub) => !DISABLED_SUBCATEGORY_IDS.has(sub.id)
      ),
    },
  ])
);

// Should this listing be kept out of the marketplace? True when its
// sub-category is switched off — or, for older listings that have no
// sub-category of their own, when its seller was registered directly under a
// switched-off one.
export const isListingHiddenFromMarketplace = (listing: any) => {
  if (isSubcategoryDisabled(listing?.subcategory)) return true;
  const sellerApp = Array.isArray(listing?.seller_applications)
    ? listing.seller_applications[0]
    : listing?.seller_applications;
  return !listing?.subcategory && isSubcategoryDisabled(sellerApp?.category);
};

// Flat lookup: raw subcategory id -> its parent category. Used to resolve
// a listing's/seller's stored category string back to the broad group it
// belongs to (sellers may also be stored directly under a parent id).
// Built from the FULL taxonomy so switched-off ids still resolve.
export const SUBCATEGORY_TO_PARENT: Record<string, string> = Object.values(
  ALL_CATEGORIES
).reduce((acc, parent) => {
  parent.subcategories.forEach((sub) => {
    acc[sub.id] = parent.id;
  });
  return acc;
}, {} as Record<string, string>);

export const DEFAULT_CATEGORY_THEME = {
  color: "#9B4DFF",
  statement: "Quality campus finds & services.",
};

// Resolves a raw category string (e.g. "laundry", or a parent id like
// "home_services") to its parent CATEGORIES entry. Returns undefined if
// completely unrecognized.
export const getParentCategoryForRaw = (rawCategory?: string) => {
  const key = (rawCategory || "").toLowerCase();
  const parentId = SUBCATEGORY_TO_PARENT[key] || key;
  return CATEGORIES[parentId];
};

export const getThemeColorForRawCategory = (rawCategory?: string) => {
  return (
    getParentCategoryForRaw(rawCategory)?.color || DEFAULT_CATEGORY_THEME.color
  );
};

export const getCategoryAbbrForRawCategory = (rawCategory?: string) => {
  return getParentCategoryForRaw(rawCategory)?.abbr || "";
};

export const getCategoryDisplayLabel = (rawCategory?: string) => {
  const parent = getParentCategoryForRaw(rawCategory);
  if (parent) return parent.name;
  return rawCategory || "";
};

// Flat lookup: subcategory id -> its proper-case display name (e.g.
// "pet_sitting" -> "Pet Sitting"). Used to show a listing's specific
// subcategory on buyer-facing cards, distinct from the broad parent
// category the corner badge already communicates.
export const SUBCATEGORY_ID_TO_NAME: Record<string, string> = Object.values(
  ALL_CATEGORIES
).reduce((acc, parent) => {
  parent.subcategories.forEach((sub) => {
    acc[sub.id] = sub.name;
  });
  return acc;
}, {} as Record<string, string>);

export const getSubcategoryDisplayName = (rawSubcategory?: string) => {
  if (!rawSubcategory) return "";
  return SUBCATEGORY_ID_TO_NAME[rawSubcategory.toLowerCase()] || "";
};
